import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/Card';
import { FormGroup, Input, Button, Select } from '../components/Form';
import { getAppSetting, setAppSetting, getGHLPipelines, getGHLSourcesList, setGHLSourcesList, getLeadSourcesConfig, setLeadSourcesConfig, invalidateWeightsCache, getProducts, invalidateProdutosCache, salvarVersaoPontuacao, listarVersoesPontuacao, listarFeriados, salvarFeriado, removerFeriado, hojeSP } from '../services/api';
import { toNewConfig, pontoDaAcao, cargoPontuaAcao, cargoNormalize, DEFAULT_CONFIG, configVigenteEm, DEFAULT_BONUS, arredondaPontos, statusDaAcao, acoesDesativadasEntre, ACOES_PONTE_GHL, diagnosticarProdutosCloser, verificarInvariantesCloser, pesoVendaCloser, auditarVendasCloser, resumirLoteRecusado, classificarZerosVendaCloser } from '../services/pontosConfig';
import { resolverProdutoOuFallback, avisarSeAdivinhou } from '../services/produtoResolver';
import { sincronizarCatalogoProdutosGhl } from '../services/ghlIntegracao';
import { getCargos } from '../services/cargos';
import { supabase } from '../lib/supabase';

/**
 * Data do cutover para o fluxo GHL: a partir daqui as reuniões são gravadas em `ghl_reunioes`
 * e `bd_agendamentos` ficou CONGELADA (só as linhas `source_system='desk_legacy'`).
 * Espelha `GHL_METRICS_CUTOFF` do dashboard-orbion. Usado para bloquear recálculos de pontuação
 * que leriam a tabela congelada e apagariam pontos vindos do GHL.
 */
const GHL_CUTOVER = '2026-07-08';

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_METAS = {
    closer: {
        daily:           { agendamentos: 5,   faturamento: 5000,   taxa_comparecimento: 70, taxa_conversao: 30 },
        weekly:          { agendamentos: 25,  faturamento: 25000,  taxa_comparecimento: 70, taxa_conversao: 30 },
        commercial_week: { agendamentos: 25,  faturamento: 25000,  taxa_comparecimento: 70, taxa_conversao: 30 },
        monthly:         { agendamentos: 100, faturamento: 100000, taxa_comparecimento: 70, taxa_conversao: 30 },
    },
    sdr: {
        daily:           { agendamentos: 5,   faturamento: 0, taxa_comparecimento: 70, taxa_conversao: 20 },
        weekly:          { agendamentos: 25,  faturamento: 0, taxa_comparecimento: 70, taxa_conversao: 20 },
        commercial_week: { agendamentos: 25,  faturamento: 0, taxa_comparecimento: 70, taxa_conversao: 20 },
        monthly:         { agendamentos: 100, faturamento: 0, taxa_comparecimento: 70, taxa_conversao: 20 },
    },
};

// Schema novo { acoes, closer } — fonte única em src/services/pontosConfig.js
const DEFAULT_WEIGHTS = DEFAULT_CONFIG;

const PERIODO_LABELS = {
    daily: 'Diária',
    weekly: 'Semanal',
    commercial_week: 'Semana Comercial',
    monthly: 'Mensal',
};

const PRODUTO_LABELS = {
    performance: 'Performance',
    expansao: 'Expansão',
    tracao: 'Tração',
    pcl: 'PCL',
};

const DEFAULT_PRODUTOS_CONFIG = [
    { slug: 'performance', label: 'Performance', keywords: ['performance'] },
    { slug: 'expansao',    label: 'Expansão',    keywords: ['expans'] },
    { slug: 'tracao',      label: 'Tração',      keywords: ['tra'] },
    { slug: 'pcl',         label: 'PCL',         keywords: [] },
];

const PAGAMENTO_LABELS = {
    parcelado_pix: 'Parc. Pix',
    parcelado_boleto: 'Parc. Boleto',
    caixa_20_30: 'Caixa 20-30%',
    caixa_31_50: 'Caixa 31-50%',
    parcelado_cartao: 'Parc. Cartão',
    a_vista: 'À Vista',
};

/**
 * Quantas linhas VIVAS cada ação tem hoje em `bd_pontos`, e quantas delas carregam
 * `observacao='ghl-bridge'`. As segundas são as que o sweep do bridge apaga sozinho no ciclo
 * seguinte se a ação for desativada — desativar sem esse número é decidir às cegas.
 *
 * Pagina porque o PostgREST corta em 1000 linhas sem avisar e `bd_pontos` já passou disso;
 * um select cru daria contagem silenciosamente truncada, que é pior que nenhuma.
 */
async function carregarInventarioPontos() {
    const PASSO = 1000;
    const inv = {};
    // Linhas `venda_closer` vivas valendo 0, guardadas inteiras para a classificação do G6
    // (quantas são 0 por escolha e quantas o cálculo não soube produzir). São dezenas, não
    // milhares — segurar isso em memória é mais barato que uma segunda varredura.
    const zerosVendaCloser = [];
    for (let de = 0; ; de += PASSO) {
        const { data, error } = await supabase
            .from('bd_pontos')
            .select('acao, observacao, pontos')
            .is('canceled_at', null)
            .range(de, de + PASSO - 1);
        if (error) throw error;
        for (const linha of data ?? []) {
            const alvo = inv[linha.acao] ?? (inv[linha.acao] = { vivas: 0, bridge: 0, zeros: 0 });
            alvo.vivas += 1;
            if (linha.observacao === 'ghl-bridge') alvo.bridge += 1;
            if (Number(linha.pontos) === 0) {
                alvo.zeros += 1;
                if (linha.acao === 'venda_closer') zerosVendaCloser.push(linha);
            }
        }
        if ((data?.length ?? 0) < PASSO) break;
    }
    return { porAcao: inv, zerosVendaCloser };
}

/** Ordem de exibição na tabela Ação × Cargo. Também é a fonte do rótulo nos avisos de save. */
const ACOES_TABELA = [
    { key: 'agendaCriada', label: 'Agenda Criada (Novo)' },
    { key: 'reagendamento', label: 'Reagendamento' },
    { key: 'agendamentoTransferido', label: 'Agendamento de Transferido' },
    { key: 'reuniaoInstantanea', label: 'Reunião Instantânea' },
    { key: 'agendaRealizada', label: 'Agenda Realizada' },
    { key: 'vendaRealizada', label: 'Venda Realizada' },
    { key: 'estrelaBonus', label: 'Estrela Bônus (por estrela)' },
    { key: 'contatoColetado', label: 'Contato Coletado (Social Seller)' },
];
const ACAO_LABELS = Object.fromEntries(ACOES_TABELA.map(({ key, label }) => [key, label]));

const ROTULO_STATUS = {
    ativa: { texto: 'Ativa', cor: 'var(--primary-color, #00dc82)', fundo: 'rgba(0,220,130,0.10)' },
    'peso-zero': { texto: 'Vale 0', cor: '#e2b93b', fundo: 'rgba(226,185,59,0.10)' },
    desativada: { texto: 'Desativada', cor: 'var(--danger, #e5484d)', fundo: 'rgba(229,72,77,0.12)' },
};

// Estados da matriz `closer` (`venda_closer`). Mesma ideia do ROTULO_STATUS acima: o 0 na
// célula tem mais de uma causa e a tela precisa dizer qual.
const ROTULO_PRODUTO = {
    ativo: { texto: 'Ativo', cor: 'var(--primary-color, #00dc82)', fundo: 'rgba(0,220,130,0.10)' },
    'peso-zero': { texto: 'Vale 0', cor: '#e2b93b', fundo: 'rgba(226,185,59,0.10)' },
    'sem-matriz': { texto: 'Sem tabela', cor: 'var(--danger, #e5484d)', fundo: 'rgba(229,72,77,0.12)' },
    'matriz-incompleta': { texto: 'Tabela incompleta', cor: 'var(--danger, #e5484d)', fundo: 'rgba(229,72,77,0.12)' },
    inalcancavel: { texto: 'Inalcançável', cor: 'var(--danger, #e5484d)', fundo: 'rgba(229,72,77,0.12)' },
};
const AVISO_PRODUTO = {
    'sem-matriz': 'Este produto não tem linha própria na tabela abaixo: a venda dele pontua 0 e nenhum erro aparece.',
    'matriz-incompleta': 'Há célula vazia nesta linha. Venda que chegar por essa forma de pagamento pontua 0 e nenhum erro aparece.',
    inalcancavel: 'Nenhuma venda chega a este produto — sem palavra-chave, o nome do produto cai no último da lista. Esta linha não é usada por ninguém.',
};

function deepMerge(defaults, overrides) {
    if (!overrides || typeof overrides !== 'object') return defaults;
    const result = { ...defaults };
    for (const key of Object.keys(defaults)) {
        if (key in overrides) {
            result[key] = typeof defaults[key] === 'object' && defaults[key] !== null
                ? deepMerge(defaults[key], overrides[key])
                : overrides[key];
        }
    }
    return result;
}

// ─── Helpers de migração ──────────────────────────────────────────────────────

// Migração em lote: mesma resolução do caminho de escrita (`api.js`), agora delegada à fonte
// única `produtoResolver.js`. Comportamento inalterado no Deploy A — o `console.warn`
// `[produto]` só denuncia, em massa, quantas linhas do histórico tiveram o slug ADIVINHADO
// pelo fallback. É o número que justifica recusar em vez de adivinhar no Deploy B.
function normalizeProductMig(produto, produtos) {
    const list = Array.isArray(produtos) && produtos.length > 0 ? produtos : DEFAULT_PRODUTOS_CONFIG;
    const r = resolverProdutoOuFallback(produto, list);
    const slug = r.slug ?? 'pcl';
    avisarSeAdivinhou('MetasPontuacao.normalizeProductMig', produto, r, slug);
    return slug;
}

function classifyPaymentMig(parcelas, formaPagamento, valorTotal, valorEntrada) {
    const numParcelas = parseInt(parcelas) || 1;
    if (numParcelas <= 1) return 'a_vista';
    // Se temos valor_entrada, podemos determinar a categoria caixa
    if (valorEntrada > 0 && valorTotal > 0) {
        const pct = (valorEntrada / valorTotal) * 100;
        if (pct >= 31) return 'caixa_31_50';
        if (pct >= 20) return 'caixa_20_30';
    }
    const forma = (formaPagamento || '').toLowerCase();
    if (forma.includes('cart') || forma.includes('crédit') || forma.includes('credit')) return 'parcelado_cartao';
    if (forma.includes('boleto')) return 'parcelado_boleto';
    return 'parcelado_pix';
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function MetasPontuacao({ embedded = false, section = null }) {
    const [metas, setMetas] = useState(DEFAULT_METAS);
    const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
    const [produtos, setProdutos] = useState(DEFAULT_PRODUTOS_CONFIG);
    const [loadingMetas, setLoadingMetas] = useState(true);
    const [loadingWeights, setLoadingWeights] = useState(true);
    const [savingMetas, setSavingMetas] = useState(false);
    const [savingWeights, setSavingWeights] = useState(false);
    const [metasSaved, setMetasSaved] = useState(false);
    const [weightsSaved, setWeightsSaved] = useState(false);
    // Resultado do empurrão do catálogo para o GHL. Fica visível na tela em vez de virar
    // console.warn: se o dropdown do CRM não recebeu o produto novo, quem registra a venda não
    // consegue escolhê-lo — e isso precisa ser sabido na hora, não descoberto na próxima venda.
    const [syncGhl, setSyncGhl] = useState(null);

    const [periodo, setPeriodo] = useState('monthly');

    const [leadConfig, setLeadConfig] = useState({});
    const [pipelines, setPipelines] = useState([]);
    const [loadingLeadConfig, setLoadingLeadConfig] = useState(true);
    const [savingLeadConfig, setSavingLeadConfig] = useState(false);
    const [leadConfigSaved, setLeadConfigSaved] = useState(false);
    const [novaFonte, setNovaFonte] = useState('');

    // ── Fontes de Lead (seção independente) ──────────────────────────────────
    const [sourcesList, setSourcesList] = useState([]);
    const [loadingSources, setLoadingSources] = useState(true);
    const [savingSources, setSavingSources] = useState(false);
    const [sourcesSaved, setSourcesSaved] = useState(false);
    const [novaFonteSource, setNovaFonteSource] = useState('');
    const [cargos, setCargos] = useState([]);

    // ── Vigência da pontuação ────────────────────────────────────────────────
    // Toda alteração de peso vale A PARTIR de uma data. O padrão é hoje, para que mexer aqui
    // nunca reescreva pontuação já publicada — que foi exatamente o que aconteceu em agosto/2026.
    const [vigenciaInicio, setVigenciaInicio] = useState(hojeSP());
    const [versoes, setVersoes] = useState([]);
    const [feriados, setFeriados] = useState([]);
    const [novoFeriado, setNovoFeriado] = useState({ data: '', nome: '', escopo: 'nacional' });

    // Inventário de linhas vivas por ação. `null` = ainda não carregou (ou falhou): a tela
    // mostra o estado da ação mesmo assim, só não consegue dizer quanto histórico está em jogo.
    const [inventario, setInventario] = useState(null);

    // Estado de cada linha da matriz de Closers. Depende de `produtos` (as keywords decidem qual
    // produto uma venda alcança) E de `weights.closer` (quem tem tabela de peso) — mudar
    // qualquer um dos dois pode transformar uma linha viva em decorativa.
    const diagnosticoProdutos = useMemo(
        () => Object.fromEntries(
            diagnosticarProdutosCloser(produtos, weights.closer).map((d) => [d.slug, d]),
        ),
        [produtos, weights.closer],
    );

    // O mesmo diagnóstico visto como invariante, não como rótulo: `bloqueios` impede o save e
    // acende o aviso do topo. Rótulo por si só não desarma nada — a config de hoje já estava
    // rotulada e continuava valendo, e a próxima venda continuava pontuando 0.
    const invariantesCloser = useMemo(
        () => verificarInvariantesCloser(produtos, weights.closer),
        [produtos, weights.closer],
    );

    // O G6 visto da tela: das vendas que já valem 0, quantas alguém escolheu que valessem 0.
    // `invariantesCloser` olha para a frente (o que a config VAI gravar); isto olha para trás
    // (o que ela JÁ gravou). Sem o segundo número, corrigir a matriz parece resolver o passado —
    // e não resolve: `venda_closer` não está em ACOES_PONTE, ninguém a recalcula.
    const zerosVenda = useMemo(
        () => (inventario ? classificarZerosVendaCloser(inventario.zerosVendaCloser, produtos, weights.closer) : null),
        [inventario, produtos, weights.closer],
    );

    useEffect(() => {
        getAppSetting('metas_config')
            .then((val) => { if (val) setMetas(deepMerge(DEFAULT_METAS, val)); })
            .catch(() => {})
            .finally(() => setLoadingMetas(false));

        getAppSetting('gamification_weights_rankings')
            .then((val) => setWeights(toNewConfig(val)))
            .catch(() => setWeights(DEFAULT_CONFIG))
            .finally(() => setLoadingWeights(false));

        listarVersoesPontuacao().then(setVersoes).catch(() => setVersoes([]));
        listarFeriados().then(setFeriados).catch(() => setFeriados([]));
        carregarInventarioPontos().then(setInventario).catch(() => setInventario(null));

        getCargos({ apenasAtivos: true })
            .then((val) => setCargos(Array.isArray(val) ? val : []))
            .catch(() => setCargos([]));

        getProducts()
            .then((val) => { if (val && val.length > 0) setProdutos(val); })
            .catch(() => {});

        getGHLPipelines().then(setPipelines).catch(() => {});

        getLeadSourcesConfig()
            .then((val) => { if (val) setLeadConfig(val); })
            .catch(() => {})
            .finally(() => setLoadingLeadConfig(false));

        getGHLSourcesList()
            .then((val) => setSourcesList(val))
            .catch(() => {})
            .finally(() => setLoadingSources(false));
    }, []);

    const handleLeadConfigChange = (fonte, field, value) => {
        setLeadConfig((prev) => ({
            ...prev,
            [fonte]: { ...prev[fonte], [field]: value, ...(field === 'pipelineId' ? { pipelineStageId: '' } : {}) },
        }));
    };

    const handleSaveLeadConfig = async () => {
        setSavingLeadConfig(true);
        try {
            await setLeadSourcesConfig(leadConfig);
            setLeadConfigSaved(true);
            setTimeout(() => setLeadConfigSaved(false), 3000);
        } catch {
            // silent — user can retry
        } finally {
            setSavingLeadConfig(false);
        }
    };

    const handleAddFonte = () => {
        const nome = novaFonte.trim();
        if (!nome || leadConfig[nome]) return;
        setLeadConfig((prev) => ({ ...prev, [nome]: { pipelineId: '', pipelineStageId: '' } }));
        setNovaFonte('');
    };

    const handleRemoveFonte = (fonte) => {
        setLeadConfig((prev) => {
            const next = { ...prev };
            delete next[fonte];
            return next;
        });
    };

    // ── Handlers: Fontes de Lead ──────────────────────────────────────────────
    const handleAddSource = () => {
        const nome = novaFonteSource.trim();
        if (!nome || sourcesList.includes(nome)) return;
        setSourcesList((prev) => [...prev, nome].sort());
        setNovaFonteSource('');
    };

    const handleRemoveSource = (fonte) => {
        setSourcesList((prev) => prev.filter((s) => s !== fonte));
    };

    const handleSaveSourcesList = async () => {
        setSavingSources(true);
        try {
            await setGHLSourcesList(sourcesList);
            setSourcesSaved(true);
            setTimeout(() => setSourcesSaved(false), 3000);
        } catch { /* silent */ } finally {
            setSavingSources(false);
        }
    };

    // Metas da Empresa: grava nos dois cargos simultaneamente para
    // manter compatibilidade com o dashboard (que lê closer/sdr separado)
    const handleMetaChange = (field, value) => {
        const num = parseFloat(value) || 0;
        setMetas((prev) => ({
            closer: {
                ...prev.closer,
                [periodo]: { ...prev.closer[periodo], [field]: num },
            },
            sdr: {
                ...prev.sdr,
                [periodo]: { ...prev.sdr[periodo], [field]: num },
            },
        }));
    };

    const [novoProdutoLabel, setNovoProdutoLabel] = useState('');
    const [novoProdutoKeywords, setNovoProdutoKeywords] = useState('');

    const handleAddProduto = () => {
        const label = novoProdutoLabel.trim();
        if (!label) return;
        const slug = label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        if (produtos.some(p => p.slug === slug)) return;
        const keywords = novoProdutoKeywords.split(',').map(k => k.trim().toLowerCase()).filter(Boolean);
        setProdutos(prev => [...prev, { slug, label, keywords }]);
        setWeights(prev => ({
            ...prev,
            closer: { ...prev.closer, [slug]: { parcelado_pix: 0, parcelado_boleto: 0, caixa_20_30: 0, caixa_31_50: 0, parcelado_cartao: 0, a_vista: 0 } },
        }));
        setNovoProdutoLabel('');
        setNovoProdutoKeywords('');
    };

    const handleRemoveProduto = (slug) => {
        if (produtos.length <= 1) return;
        setProdutos(prev => prev.filter(p => p.slug !== slug));
        setWeights(prev => {
            const next = { ...prev.closer };
            delete next[slug];
            return { ...prev, closer: next };
        });
    };

    /**
     * Cria o bloco que falta na matriz `closer` — o conserto do estado `sem-matriz`.
     * Zerado de propósito: o botão troca "0 porque o cálculo não achou tabela" por "0 porque
     * alguém escolheu 0", que é a diferença entre defeito e configuração. Preencher os valores
     * é o passo seguinte, na própria linha.
     */
    const handleCriarTabelaCloser = useCallback((slug, label) => {
        const ok = window.confirm(
            `Criar a tabela de pesos de "${label}" com todos os valores em 0.\n\n` +
            `Hoje a venda deste produto grava 0 porque o cálculo não encontra tabela nenhuma — ` +
            `um zero que ninguém escolheu e que não aparece como erro em lugar nenhum.\n` +
            `Depois disto ela grava 0 porque a tabela diz 0. Preencha os valores na linha antes ` +
            `de salvar se a venda deve pontuar.\n\nCriar?`,
        );
        if (!ok) return;
        setWeights((prev) => ({
            ...prev,
            closer: {
                ...prev.closer,
                [slug]: Object.fromEntries(Object.keys(PAGAMENTO_LABELS).map((pag) => [pag, 0])),
            },
        }));
    }, []);

    const handleWeightCloserChange = (produto, pagamento, value) => {
        setWeights((prev) => ({
            ...prev,
            closer: {
                ...prev.closer,
                [produto]: {
                    ...prev.closer[produto],
                    [pagamento]: parseFloat(value) || 0,
                },
            },
        }));
    };

    // Colunas de cargo (dinâmicas de bd_cargos, normalizadas), sempre incluindo os slugs default.
    const cargoColunas = (() => {
        const map = new Map();
        for (const c of cargos) { const s = cargoNormalize(c.nome); if (s && !map.has(s)) map.set(s, c.nome); }
        for (const s of ['sdr', 'sales_farmer', 'social_seller', 'closer']) if (!map.has(s)) map.set(s, s);
        return [...map.entries()].map(([slug, label]) => ({ slug, label }));
    })();

    const handleAcaoDefaultChange = (acao, value) => {
        setWeights((prev) => ({ ...prev, acoes: { ...prev.acoes, [acao]: { ...prev.acoes[acao], default: parseFloat(value) || 0 } } }));
    };
    const handleAcaoCargoToggle = (acao, slug) => {
        setWeights((prev) => {
            const a = prev.acoes[acao];
            const nextCargos = a.cargos.includes(slug) ? a.cargos.filter((x) => x !== slug) : [...a.cargos, slug];
            return { ...prev, acoes: { ...prev.acoes, [acao]: { ...a, cargos: nextCargos } } };
        });
    };
    const handleAcaoOverrideChange = (acao, slug, value) => {
        setWeights((prev) => {
            const a = prev.acoes[acao];
            const override = { ...a.override };
            if (value === '' || value === null) delete override[slug]; // vazio → usa default
            else override[slug] = parseFloat(value); // aceita 0
            return { ...prev, acoes: { ...prev.acoes, [acao]: { ...a, override } } };
        });
    };

    const saveMetas = useCallback(async () => {
        setSavingMetas(true);
        try {
            await setAppSetting('metas_config', metas);
            setMetasSaved(true);
            setTimeout(() => setMetasSaved(false), 2500);
        } catch {
            alert('Erro ao salvar metas.');
        } finally {
            setSavingMetas(false);
        }
    }, [metas]);

    const handleBonusChange = (campo, valor) => {
        setWeights((prev) => ({ ...prev, bonus: { ...DEFAULT_BONUS, ...(prev.bonus ?? {}), [campo]: valor } }));
    };

    /**
     * Frase de aviso para uma ação que está perdendo cargo, com o histórico em jogo.
     * Separada do handler porque o que ela diz depende de a ação estar ou não sob o sweep:
     * ação da ponte perde o histórico já gravado; ação fora da ponte só deixa de pontuar.
     */
    const avisoDeDesativacao = useCallback(({ acao, perdidos, desativouDeVez }) => {
        const inv = inventario?.porAcao?.[acao] ?? { vivas: 0, bridge: 0, zeros: 0 };
        const rotulo = ACAO_LABELS[acao] ?? acao;
        const alvo = desativouDeVez ? 'DESATIVADA (nenhum cargo pontua)' : `sem os cargos: ${perdidos.join(', ')}`;
        const linhas = [`• ${rotulo} → ${alvo}`, `    ${inv.vivas} linha(s) viva(s) hoje em bd_pontos.`];
        if (ACOES_PONTE_GHL.includes(acao)) {
            linhas.push(inv.bridge > 0
                ? `    ${inv.bridge} dela(s) têm observacao='ghl-bridge' e serão APAGADAS pelo sweep no próximo ciclo (1 min).`
                : '    Nenhuma linha ghl-bridge hoje, mas as futuras deixam de ser geradas.');
        } else {
            linhas.push('    Fora do sweep: o histórico fica, só param de ser geradas linhas novas.');
        }
        return linhas.join('\n');
    }, [inventario]);

    const saveWeights = useCallback(async () => {
        // GUARDA DE ZERO ACIDENTAL — invariante, não aviso. Salvar uma matriz `closer` em que
        // algum produto alcançável não tem bloco é assinar o `?? 0` de api.js:566: a venda grava
        // 0 e nada no sistema distingue isso de "vale zero". Não há confirmação porque não há
        // caso legítimo — criar o bloco zerado (botão na própria linha) já resolve e transforma
        // o 0 em escolha registrada. Foi este estado que produziu as 10 vendas em 0 da §7.1.
        if (invariantesCloser.bloqueios.length > 0) {
            const detalhe = invariantesCloser.bloqueios
                .map((b) => `• ${b.resumo}\n    → ${b.comoResolver}`)
                .join('\n\n');
            alert(
                `Não dá para salvar: esta configuração faz venda pontuar 0 sem que ninguém tenha escolhido 0.\n\n${detalhe}\n\n` +
                'Use o botão "Criar tabela" na linha do produto, na tabela de Closers acima.',
            );
            return;
        }

        // AVISO DE LINHA DECORATIVA — peso configurado que nenhuma venda alcança. Aqui cabe
        // confirmar: produto aposentado é caso legítimo, produto que perdeu a palavra-chave não.
        if (invariantesCloser.avisos.length > 0) {
            const detalhe = invariantesCloser.avisos
                .map((a) => `• ${a.resumo}\n    → ${a.comoResolver}`)
                .join('\n\n');
            const ok = window.confirm(
                `Esta configuração tem peso que nenhuma venda alcança.\n\n${detalhe}\n\nSalvar assim mesmo?`,
            );
            if (!ok) return;
        }

        // GUARDA DE DESATIVAÇÃO — desmarcar todos os cargos de uma ação não é "peso 0": a ação
        // sai do cálculo e, se for da ponte GHL, o delete de reconciliação leva o histórico
        // `ghl-bridge` dela junto no ciclo seguinte. Foi assim que agendaRealizada, estrelaBonus,
        // reagendamento e vendaRealizada ficaram desligadas desde 2026-07-08 sem ninguém notar.
        const anterior = configVigenteEm(versoes, vigenciaInicio)?.value;
        const perdas = anterior ? acoesDesativadasEntre(toNewConfig(anterior), weights) : [];
        if (perdas.length > 0) {
            const detalhe = perdas.map(avisoDeDesativacao).join('\n\n');
            const carregou = inventario !== null;
            const ok = window.confirm(
                `Esta configuração TIRA CARGO de ação que hoje pontua.\n\n${detalhe}\n\n` +
                (carregou ? '' : 'ATENÇÃO: a contagem de linhas vivas não carregou — os números acima são 0 por falta de leitura, não por ausência de histórico.\n\n') +
                `Vale a partir de ${vigenciaInicio}. Confirmar?`
            );
            if (!ok) return;
        }
        setSavingWeights(true);
        try {
            // Duas escritas com papéis diferentes:
            //  - a versão com vigência é o que os cálculos de ponto leem (por data);
            //  - app_settings continua sendo o "valor corrente", lido por telas e integrações
            //    que só querem saber quanto vale hoje.
            await salvarVersaoPontuacao(weights, vigenciaInicio, `Salvo pela tela de Metas & Pontuação (vigência ${vigenciaInicio})`);
            await Promise.all([
                setAppSetting('gamification_weights_rankings', weights),
                setAppSetting('produtos_config', produtos),
            ]);
            invalidateWeightsCache();
            invalidateProdutosCache();
            setVersoes(await listarVersoesPontuacao());
            setWeightsSaved(true);
            setTimeout(() => setWeightsSaved(false), 2500);

            // O catálogo do desk manda no dropdown do GHL. Sem este empurrão o fluxo era só de
            // entrada — produto nascia no CRM e chegava aqui sem cadastro, e era assim que uma
            // venda de "E-commerce" entrava valendo a comissão de outro produto.
            //
            // DEPOIS do salvamento, e sem derrubá-lo: a config local já está gravada e correta;
            // se o CRM estiver fora do ar, o gestor precisa saber, não perder o que acabou de
            // salvar. Por isso o erro vira aviso, não exceção.
            try {
                const r = await sincronizarCatalogoProdutosGhl(produtos);
                if (r?.adicionadas?.length) setSyncGhl({ tipo: 'ok', texto: `Catálogo enviado ao GHL: ${r.adicionadas.join(', ')} agora aparece(m) no campo "Produto Vendido".` });
                else setSyncGhl({ tipo: 'ok', texto: 'Catálogo já estava em sincronia com o GHL.' });
            } catch (e) {
                setSyncGhl({ tipo: 'erro', texto: `Pontuação salva, mas o catálogo NÃO foi enviado ao GHL: ${e?.message ?? e}. Um produto que só existe aqui não aparece para quem registra a venda.` });
            }
        } catch (e) {
            alert(`Erro ao salvar pontuação: ${e?.message ?? e}`);
        } finally {
            setSavingWeights(false);
        }
    }, [weights, produtos, vigenciaInicio, versoes, inventario, avisoDeDesativacao, invariantesCloser]);

    const adicionarFeriado = useCallback(async () => {
        if (!novoFeriado.data || !novoFeriado.nome.trim()) {
            alert('Preencha a data e o nome do feriado.');
            return;
        }
        try {
            await salvarFeriado({ ...novoFeriado, nome: novoFeriado.nome.trim() });
            setFeriados(await listarFeriados());
            setNovoFeriado({ data: '', nome: '', escopo: 'nacional' });
        } catch (e) {
            alert(`Erro ao salvar feriado: ${e?.message ?? e}`);
        }
    }, [novoFeriado]);

    const alternarFeriado = useCallback(async (f) => {
        try {
            await salvarFeriado({ ...f, ativo: !f.ativo });
            setFeriados(await listarFeriados());
        } catch (e) {
            alert(`Erro ao atualizar feriado: ${e?.message ?? e}`);
        }
    }, []);

    const excluirFeriado = useCallback(async (data) => {
        try {
            await removerFeriado(data);
            setFeriados(await listarFeriados());
        } catch (e) {
            alert(`Erro ao remover feriado: ${e?.message ?? e}`);
        }
    }, []);

    const currentMeta = metas.closer?.[periodo] ?? DEFAULT_METAS.closer.monthly;

    // ─── Migração histórica ───────────────────────────────────────────────────
    const [migrating, setMigrating] = useState(false);
    const [migrateMsg, setMigrateMsg] = useState('');
    const [showMigrateConfirm, setShowMigrateConfirm] = useState(false);
    const [migrateInicio, setMigrateInicio] = useState('');
    const [migrateFim, setMigrateFim] = useState('');
    const [migrateGrupo, setMigrateGrupo] = useState('todos'); // 'todos' | 'closer' | 'sdr'

    const migrarPontosHistoricos = useCallback(async () => {
        setMigrating(true);
        setMigrateMsg('');
        if (!migrateInicio || !migrateFim || migrateInicio > migrateFim) {
            setMigrateMsg('Preencha o período (data início e data fim) antes de migrar.');
            setMigrating(false);
            return;
        }
        // GUARDA DE SEGURANÇA — não remover sem portar a função para ghl_reunioes.
        // Esta função APAGA bd_pontos no período (delete + reinsert) e recalcula a partir de
        // bd_agendamentos, que está CONGELADA em 2026-07-07: as reuniões novas vivem em
        // ghl_reunioes (source_system='ghl') e não existem lá. Rodar em período posterior ao
        // corte apagaria a pontuação vinda do GHL sem repor nada.
        if (migrateFim >= GHL_CUTOVER) {
            setMigrateMsg(
                `Bloqueado: a partir de ${GHL_CUTOVER} as reuniões vivem em ghl_reunioes, não em ` +
                `bd_agendamentos. Recalcular por aqui APAGARIA a pontuação vinda do GHL. ` +
                `Use apenas para períodos que terminem antes de ${GHL_CUTOVER}.`
            );
            setMigrating(false);
            return;
        }
        // GUARDA DE VIGÊNCIA — esta função apaga e recria bd_pontos no período. Recalcular com
        // o peso de hoje é o defeito que a vigência veio corrigir, então ela exige que exista
        // versão cobrindo o início do período. Sem isso, os pesos de hoje entrariam em pontos
        // antigos e o ranking mudaria por baixo de quem já viu o resultado.
        const versoesMig = await listarVersoesPontuacao();
        if (!configVigenteEm(versoesMig, migrateInicio)) {
            const maisAntiga = versoesMig.map((v) => v.vigencia_inicio).sort()[0];
            setMigrateMsg(
                `Bloqueado: não há configuração de pontuação com vigência em ${migrateInicio}. ` +
                (maisAntiga
                    ? `A vigência mais antiga cadastrada começa em ${maisAntiga}. Cadastre a config da época (com a data de início correta) antes de recalcular esse período.`
                    : 'Nenhuma vigência cadastrada — rode a migration de vigência primeiro.')
            );
            setMigrating(false);
            return;
        }
        const feriadosMig = new Set((await listarFeriados()).filter((f) => f.ativo).map((f) => String(f.data).slice(0, 10)));
        /** Config vigente na data do ponto — não a config de hoje. */
        const cfgEm = (dataISO) => toNewConfig(configVigenteEm(versoesMig, dataISO)?.value);

        try {
            // Montar queries com filtro de data opcional
            let agendQuery = supabase.from('bd_agendamentos').select('*');
            let vendaQuery = supabase.from('bd_vendas_faturamento').select('*').eq('status', 'approved').is('canceled_at', null);
            if (migrateInicio) {
                agendQuery = agendQuery.gte('data_agendamento', migrateInicio);
                vendaQuery = vendaQuery.gte('data_venda', migrateInicio);
            }
            if (migrateFim) {
                agendQuery = agendQuery.lte('data_agendamento', migrateFim);
                vendaQuery = vendaQuery.lte('data_venda', migrateFim);
            }

            const [{ data: agendamentos }, { data: vendas }, { data: pessoas }, { data: comissoes }] = await Promise.all([
                agendQuery,
                vendaQuery,
                supabase.from('bd_pessoas').select('nome, cargo'),
                supabase.from('bd_comissoes').select('pessoa, cargo, data_base'),
            ]);

            const cargoMap = Object.fromEntries((pessoas || []).map(p => [p.nome, p.cargo]));

            // Cargo histórico de comissões: pessoa|data_base → cargo do momento
            const comissaoCargoMap = {};
            for (const c of comissoes || []) {
                if (!c.cargo) continue;
                const key = `${c.pessoa}|${c.data_base}`;
                if (!comissaoCargoMap[key]) comissaoCargoMap[key] = c.cargo;
            }

            const rows = [];
            // Cada venda_closer que o lote produziria, para a auditoria antes da escrita.
            // Guardada à parte de `rows` porque o que interessa é o par (produto, condição) e a
            // config da vigência daquela data — `rows` já perdeu isso.
            const vendasCloser = [];
            const TIPO_ACAO_MAP_MIG = {
                'Novo Agendamento': 'agendaCriada',
                'Reagendamento': 'reagendamento',
                'Agendamento de Transferido': 'agendamentoTransferido',
            };
            if (migrateGrupo !== 'closer') for (const a of agendamentos || []) {
                if (!a.agendador || !a.id) continue;
                const cargo = a.cargo || cargoMap[a.agendador] || 'SDR';
                const acaoKey = TIPO_ACAO_MAP_MIG[a.tipo_agendamento];
                // Tipos não mapeados (ex: Continuação da Agenda) não geram pontos.
                // Quem pontua cada ação agora é config-driven (cargoPontuaAcao).
                const dataAg = a.data_agendamento;
                const cfg = cfgEm(dataAg);           // peso da época, não o de hoje
                const opts = { data: dataAg, feriados: feriadosMig };
                if (acaoKey && cargoPontuaAcao(cargo, acaoKey, cfg))
                    rows.push({ pessoa: a.agendador, cargo, acao: acaoKey, pontos: pontoDaAcao(cargo, acaoKey, cfg, opts), referencia_id: a.id, referencia_tabela: 'bd_agendamentos', data: dataAg });
                if (dataAg && a.data_reuniao && dataAg === a.data_reuniao && cargoPontuaAcao(cargo, 'reuniaoInstantanea', cfg))
                    rows.push({ pessoa: a.agendador, cargo, acao: 'reuniaoInstantanea', pontos: pontoDaAcao(cargo, 'reuniaoInstantanea', cfg, opts), referencia_id: a.id, referencia_tabela: 'bd_agendamentos', data: dataAg });
                if ((a.estrelas || 0) > 0) {
                    if (cargoPontuaAcao(cargo, 'agendaRealizada', cfg))
                        rows.push({ pessoa: a.agendador, cargo, acao: 'agendaRealizada', pontos: pontoDaAcao(cargo, 'agendaRealizada', cfg, opts), referencia_id: a.id, referencia_tabela: 'bd_agendamentos', data: dataAg });
                    if (cargoPontuaAcao(cargo, 'estrelaBonus', cfg))
                        rows.push({ pessoa: a.agendador, cargo, acao: 'estrelaBonus', pontos: arredondaPontos(a.estrelas * pontoDaAcao(cargo, 'estrelaBonus', cfg, opts)), referencia_id: a.id, referencia_tabela: 'bd_agendamentos', data: dataAg });
                }
            }

            for (const v of vendas || []) {
                if (!v.vendedor || !v.id) continue;
                const produto = normalizeProductMig(v.produto, produtos);
                const paymentCat = classifyPaymentMig(v.parcelas, v.forma_pagamento, v.valor_total, v.valor_entrada ?? 0);
                const cfg = cfgEm(v.data_venda);     // peso da época, não o de hoje
                if (migrateGrupo !== 'sdr') {
                    // Sem `?? 0`: `pontos` fica null quando a matriz não sabe responder, e a
                    // auditoria abaixo recusa o lote inteiro antes de qualquer escrita. Esta era
                    // a terceira cópia do `closer[...]?.[...] ?? 0` — e a única em lote.
                    vendasCloser.push({ closer: cfg.closer, slug: produto, condicao: paymentCat, rotulo: v.produto || produto, pessoa: v.vendedor, data: v.data_venda });
                    rows.push({
                        pessoa: v.vendedor,
                        cargo: comissaoCargoMap[`${v.vendedor}|${v.data_venda}`] || cargoMap[v.vendedor] || 'Closer',
                        acao: 'venda_closer',
                        pontos: pesoVendaCloser(cfg.closer, produto, paymentCat).pontos,
                        referencia_id: v.id, referencia_tabela: 'bd_vendas_faturamento',
                        data: v.data_venda,
                        observacao: `${v.produto || ''} / ${paymentCat}`,
                    });
                }
                if (v.pre_vendedor && migrateGrupo !== 'closer') {
                    const cargoPV = comissaoCargoMap[`${v.pre_vendedor}|${v.data_venda}`] || cargoMap[v.pre_vendedor] || 'SDR';
                    if (cargoPontuaAcao(cargoPV, 'vendaRealizada', cfg))
                        rows.push({
                            pessoa: v.pre_vendedor,
                            cargo: cargoPV,
                            acao: 'vendaRealizada', pontos: pontoDaAcao(cargoPV, 'vendaRealizada', cfg, { data: v.data_venda, feriados: feriadosMig }),
                            referencia_id: v.id, referencia_tabela: 'bd_vendas_faturamento',
                            data: v.data_venda,
                        });
                }
            }

            // GUARDA DE ZERO ACIDENTAL NA ESCRITA — antes do delete, porque abortar depois dele
            // seria apagar sem repor. A guarda de `saveWeights` age no salvamento da config e
            // este botão não passa por lá: ele apaga e reinsere bd_pontos de um período inteiro
            // com a config que já está gravada. Se essa config não sabe pontuar algum produto do
            // período, o resultado antes era uma leva de linhas em 0 indistinguíveis de "vale
            // zero" — o mesmo defeito das 7 vendas da §7.1, só que em lote e num clique.
            const { naoCalculadas, zerosDeclarados } = auditarVendasCloser(vendasCloser);
            if (naoCalculadas.length > 0) {
                setMigrateMsg(
                    `Bloqueado: ${naoCalculadas.length} venda(s) do período não têm peso calculável na tabela de Closers da vigência.\n\n` +
                    `${resumirLoteRecusado(naoCalculadas)}\n\n` +
                    'Corrija a tabela de pesos (seção Closers, acima) e salve antes de migrar. ' +
                    'Deixar a linha do produto toda em 0 também destrava — a diferença é que aí o 0 é escolha registrada, não falha de cálculo.'
                );
                return;
            }
            if (zerosDeclarados.length > 0) {
                const ok = window.confirm(
                    `${zerosDeclarados.length} venda(s) do período vão valer 0 porque a tabela de pesos da vigência diz 0 ` +
                    `(ex.: ${zerosDeclarados[0].rotulo} / ${zerosDeclarados[0].condicao}).\n\n` +
                    'Isto é 0 escolhido, não falha de cálculo. Continuar?'
                );
                if (!ok) {
                    setMigrateMsg('Migração cancelada — nada foi apagado nem gravado.');
                    return;
                }
            }

            // Delete escopado ao período e grupo — preserva ajustes manuais e pontos fora do intervalo.
            let delQuery = supabase.from('bd_pontos').delete().gte('data', migrateInicio).lte('data', migrateFim)
                .neq('referencia_tabela', 'ghl_reunioes'); // pontos da ponte GHL: gerenciados pelo sync, nunca pela recomputação
            if (migrateGrupo === 'closer') delQuery = delQuery.eq('acao', 'venda_closer');
            else if (migrateGrupo === 'sdr') delQuery = delQuery.neq('acao', 'manual').neq('acao', 'venda_closer');
            else delQuery = delQuery.neq('acao', 'manual');
            const { error: delError } = await delQuery;
            if (delError) throw delError;

            for (let i = 0; i < rows.length; i += 100) {
                const { error } = await supabase.from('bd_pontos').insert(rows.slice(i, i + 100));
                if (error) throw error;
            }

            const breakdown = rows.reduce((acc, r) => {
                acc[r.acao] = acc[r.acao] || { count: 0, pontos: 0 };
                acc[r.acao].count += 1;
                acc[r.acao].pontos += (r.pontos || 0);
                return acc;
            }, {});
            const breakdownMsg = Object.entries(breakdown)
                .map(([acao, { count, pontos }]) => `${acao}: ${count} (${pontos.toFixed(1)} pts)`)
                .join(' • ');
            setMigrateMsg(
                `Migração concluída (${migrateInicio} → ${migrateFim}). ` +
                `${rows.length} registro(s) reinseridos. ${breakdownMsg}`
            );
        } catch (err) {
            setMigrateMsg(`Erro: ${err.message}`);
        } finally {
            setMigrating(false);
        }
    }, [migrateInicio, migrateFim, migrateGrupo, produtos]);

    // ─── Ajuste manual ────────────────────────────────────────────────────────
    const [pessoas, setPessoas] = useState([]);
    const [ajuste, setAjuste] = useState({ pessoa: '', pontos: '', data: hojeSP(), observacao: '' });
    const [savingAjuste, setSavingAjuste] = useState(false);
    const [ajusteMsg, setAjusteMsg] = useState('');
    const [ajustesHistorico, setAjustesHistorico] = useState([]);

    useEffect(() => {
        supabase.from('bd_pessoas')
            .select('nome, cargo')
            .neq('atividade', 'inativo')
            .neq('nome', 'Sem SDR')
            .order('nome')
            .then(({ data }) => {
                if (data) setPessoas(data);
                if (data && data.length > 0) setAjuste(prev => ({ ...prev, pessoa: data[0].nome }));
            });
        carregarAjustes();
    }, []);

    const carregarAjustes = async () => {
        const { data } = await supabase.from('bd_pontos').select('id, pessoa, pontos, data, observacao, created_at')
            .eq('acao', 'manual').order('created_at', { ascending: false }).limit(50);
        if (data) setAjustesHistorico(data);
    };

    const salvarAjuste = useCallback(async () => {
        if (!ajuste.observacao.trim()) { setAjusteMsg('Observação obrigatória.'); return; }
        if (!ajuste.pontos || isNaN(parseFloat(ajuste.pontos))) { setAjusteMsg('Informe um valor de pontos.'); return; }
        setSavingAjuste(true);
        setAjusteMsg('');
        try {
            const pessoaSel = pessoas.find(p => p.nome === ajuste.pessoa);
            const { error } = await supabase.from('bd_pontos').insert({
                pessoa: ajuste.pessoa,
                cargo: pessoaSel?.cargo || '',
                acao: 'manual',
                pontos: parseFloat(ajuste.pontos),
                data: ajuste.data,
                observacao: ajuste.observacao.trim(),
            });
            if (error) throw error;
            setAjuste(prev => ({ ...prev, pontos: '', observacao: '' }));
            setAjusteMsg('Ajuste salvo!');
            setTimeout(() => setAjusteMsg(''), 3000);
            carregarAjustes();
        } catch (err) {
            setAjusteMsg(`Erro: ${err.message}`);
        } finally {
            setSavingAjuste(false);
        }
    }, [ajuste, pessoas]);

    const estornarAjuste = async (original) => {
        const pessoaSel = pessoas.find(p => p.nome === original.pessoa);
        await supabase.from('bd_pontos').insert({
            pessoa: original.pessoa,
            cargo: pessoaSel?.cargo || '',
            acao: 'manual',
            pontos: -parseFloat(original.pontos),
            data: original.data,
            observacao: `Estorno: ${original.observacao}`,
        });
        carregarAjustes();
    };

    const showMetas = !section || section === 'metas';
    const showPontuacao = !section || section === 'pontuacao';
    const showProdutos = !section || section === 'produtos';

    return (
        <>
        {!embedded && (
            <div className="page-header">
                <div className="page-header-left">
                    <div>
                        <div className="page-kicker">Configurações</div>
                        <h1 className="page-title">
                            {section === 'metas' ? 'Metas' : section === 'pontuacao' ? 'Pontuação' : 'Metas e Pontuação'}
                        </h1>
                    </div>
                </div>
            </div>
        )}
        <div className="page-body" style={embedded ? { minHeight: 'auto' } : undefined}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* ── METAS ── */}
            {showMetas && <Card>
                <CardHeader
                    title="Metas da Empresa"
                    description="Metas globais da operação por período. Exibidas no dashboard de rankings."
                />
                <CardContent>
                    {loadingMetas ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
                    ) : (
                        <>
                            {/* Tabs período */}
                            <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                                {Object.entries(PERIODO_LABELS).map(([key, label]) => (
                                    <button
                                        key={key}
                                        onClick={() => setPeriodo(key)}
                                        style={{
                                            padding: '0.3rem 0.875rem',
                                            borderRadius: '0.4rem',
                                            border: periodo === key ? '1px solid var(--primary-color)' : '1px solid rgba(255,255,255,0.08)',
                                            background: periodo === key ? 'rgba(0,220,130,0.08)' : 'transparent',
                                            color: periodo === key ? 'var(--primary-color)' : 'var(--text-secondary)',
                                            cursor: 'pointer',
                                            fontSize: '0.8125rem',
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>

                            {/* Inputs */}
                            <div className="grid-2">
                                <FormGroup label="Agendamentos">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={currentMeta.agendamentos}
                                        onChange={(e) => handleMetaChange('agendamentos', e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup label="Faturamento (R$)">
                                    <Input
                                        type="number"
                                        min="0"
                                        value={currentMeta.faturamento}
                                        onChange={(e) => handleMetaChange('faturamento', e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup label="Taxa de Comparecimento (%)">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentMeta.taxa_comparecimento}
                                        onChange={(e) => handleMetaChange('taxa_comparecimento', e.target.value)}
                                    />
                                </FormGroup>
                                <FormGroup label="Taxa de Conversão em Vendas (%)">
                                    <Input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={currentMeta.taxa_conversao}
                                        onChange={(e) => handleMetaChange('taxa_conversao', e.target.value)}
                                    />
                                </FormGroup>
                            </div>

                            <div style={{ marginTop: '1.25rem' }}>
                                <Button onClick={saveMetas} isLoading={savingMetas}>
                                    {metasSaved ? 'Salvo!' : 'Salvar Metas'}
                                </Button>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>}

            {/* ── PONTUAÇÃO + AJUSTE + MIGRAÇÃO + GHL ── */}
            {showPontuacao && <><Card>
                <CardHeader
                    title="Pesos de Pontuação"
                    description="Define quantos pontos cada ação ou venda vale no ranking de gamificação."
                />
                <CardContent>
                    {loadingWeights ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
                    ) : (
                        <>
                            {/* Zero acidental — o estado que gerou as 10 vendas em 0 da §7.1 do
                                relatório. Fica no topo porque descrever isso só na linha da
                                tabela foi o que deixou passar: a linha estava rotulada e a
                                config seguia valendo. */}
                            {invariantesCloser.bloqueios.length > 0 && (
                                <div style={{
                                    border: '1px solid rgba(229,72,77,0.45)', background: 'rgba(229,72,77,0.08)',
                                    borderRadius: '0.5rem', padding: '0.75rem 0.875rem', marginBottom: '1.25rem',
                                }}>
                                    <strong style={{ color: 'var(--danger, #e5484d)', fontSize: '0.8125rem' }}>
                                        Venda pontuando 0 sem ninguém ter escolhido 0 — valendo agora
                                    </strong>
                                    {invariantesCloser.bloqueios.map((b) => (
                                        <p key={b.slug} style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0.5rem 0 0' }}>
                                            {b.resumo} <span style={{ color: 'var(--text-primary)' }}>{b.comoResolver}</span>
                                        </p>
                                    ))}
                                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.72rem', margin: '0.5rem 0 0' }}>
                                        Enquanto isto estiver assim, a tela <strong>não salva</strong> a pontuação. Não é
                                        retroativo: corrigir vale para venda nova — <code>venda_closer</code> não é recalculada
                                        por ninguém.
                                    </p>
                                </div>
                            )}

                            {/* Closers */}
                            <h3 style={{ color: 'var(--text-primary)', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                Closers — Pontos por Venda
                            </h3>

                            {/* O que JÁ está gravado em 0. O aviso vermelho acima diz o que a config
                                vai fazer da próxima venda; este diz o que ela já fez — dois números
                                diferentes que a tela mostrava como um só (nenhum). */}
                            {zerosVenda && zerosVenda.total > 0 && (
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.75rem' }}>
                                    Hoje há <strong style={{ color: 'var(--text-primary)' }}>{zerosVenda.total}</strong> venda(s) de closer
                                    vivas valendo 0:{' '}
                                    <strong style={{ color: ROTULO_PRODUTO['peso-zero'].cor }}>{zerosVenda.zeroDeclarado}</strong> porque a
                                    tabela abaixo diz 0, e{' '}
                                    <strong style={{ color: ROTULO_PRODUTO['sem-matriz'].cor }}>{zerosVenda.naoCalculados}</strong> porque o
                                    cálculo não soube responder
                                    {Object.keys(zerosVenda.porProduto).length > 0 && ` (${Object.entries(zerosVenda.porProduto).map(([s, n]) => `${s}: ${n}`).join(', ')})`}
                                    {zerosVenda.indeterminados > 0 && `, e ${zerosVenda.indeterminados} sem observação legível`}.
                                    Corrigir a tabela <strong>não</strong> reescreve nenhuma delas.
                                </p>
                            )}
                            <div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520, fontSize: '0.8125rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Produto</th>
                                            <th style={thStyle}>Estado</th>
                                            {Object.values(PAGAMENTO_LABELS).map((l) => (
                                                <th key={l} style={thStyle}>{l}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {produtos.map(({ slug, label: prodLabel }) => {
                                            // `weights.closer[slug]?.[pag] ?? 0` desenha 0 tanto para "vale zero"
                                            // quanto para "este produto não tem tabela" — e é esse 0 ambíguo que
                                            // deixou 10 vendas pontuando 0 sem ninguém ver.
                                            const diag = diagnosticoProdutos[slug];
                                            const marca = ROTULO_PRODUTO[diag?.estado] ?? ROTULO_PRODUTO.ativo;
                                            const alerta = AVISO_PRODUTO[diag?.estado];
                                            return (
                                                <tr key={slug} style={alerta ? { background: 'rgba(229,72,77,0.04)' } : undefined}>
                                                    <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 500 }}>
                                                        {prodLabel}
                                                        {diag?.ehFallback && (
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2, maxWidth: 200, whiteSpace: 'normal' }}>
                                                                Recebe todo produto sem palavra-chave.
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '0.75rem',
                                                            fontSize: '0.7rem', fontWeight: 600, color: marca.cor, background: marca.fundo,
                                                            border: `1px solid ${marca.cor}33`,
                                                        }}>{marca.texto}</span>
                                                        {alerta && (
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2, maxWidth: 230, whiteSpace: 'normal' }}>
                                                                {alerta}
                                                            </div>
                                                        )}
                                                        {diag?.estado === 'sem-matriz' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCriarTabelaCloser(slug, prodLabel)}
                                                                style={{
                                                                    marginTop: 4, padding: '0.125rem 0.5rem', borderRadius: '0.375rem',
                                                                    fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                                                                    color: 'var(--text-primary)', background: 'transparent',
                                                                    border: '1px solid var(--danger, #e5484d)',
                                                                }}
                                                            >Criar tabela</button>
                                                        )}
                                                    </td>
                                                    {Object.keys(PAGAMENTO_LABELS).map((pag) => {
                                                        // Célula vazia ≠ célula em 0. Desenhar `?? 0` era a mesma
                                                        // ambiguidade do ledger, agora no editor: o operador via 0,
                                                        // acreditava que a venda valia 0, e o que existia era ausência
                                                        // de peso. Vazio + placeholder "—" diz que não há valor.
                                                        const matrizProduto = weights.closer[slug];
                                                        const vazia = !matrizProduto || (diag?.faltando ?? []).includes(pag);
                                                        return (
                                                            <td key={pag} style={tdStyle}>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.25"
                                                                    value={vazia ? '' : matrizProduto[pag]}
                                                                    placeholder={vazia ? '—' : undefined}
                                                                    onChange={(e) => handleWeightCloserChange(slug, pag, e.target.value)}
                                                                    style={vazia ? { ...numInputStyle, borderColor: 'var(--danger, #e5484d)' } : numInputStyle}
                                                                />
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                                    O produto de uma venda é reconhecido por <strong>palavra-chave</strong> no nome. Produto sem palavra-chave
                                    não é reconhecido: a venda cai no <strong>último produto da lista</strong>. Por isso um produto pode ficar
                                    <strong> Inalcançável</strong> (nada chega nele) ou <strong>Sem tabela</strong> (chega, mas não tem peso —
                                    a venda pontua 0 e nenhum erro aparece). As duas coisas são diferentes de <strong>Vale 0</strong>, que é peso
                                    zero escolhido de propósito.
                                </p>
                            </div>

                            {/* Pontos por Ação × Cargo */}
                            <h3 style={{ color: 'var(--text-primary)', fontSize: '0.9375rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                                Pontos por Ação × Cargo
                            </h3>
                            <div style={{ overflowX: 'auto', marginBottom: '1.25rem' }}>
                                <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: '0.8125rem' }}>
                                    <thead>
                                        <tr>
                                            <th style={thStyle}>Ação</th>
                                            <th style={thStyle}>Estado</th>
                                            <th style={thStyle}>Padrão</th>
                                            {cargoColunas.map((c) => (<th key={c.slug} style={thStyle}>{c.label}</th>))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ACOES_TABELA.map(({ key, label }) => {
                                            const a = weights.acoes?.[key] ?? { default: 0, cargos: [], override: {} };
                                            // 'desativada' ≠ 'peso-zero': a primeira tira a ação do cálculo e (se for
                                            // da ponte) faz o sweep apagar o histórico dela; a segunda continua
                                            // gravando linha, valendo 0. Até 2026-08-25 as duas apareciam iguais aqui.
                                            const status = statusDaAcao(a);
                                            const marca = ROTULO_STATUS[status];
                                            const inv = inventario?.porAcao?.[key];
                                            const sobSweep = ACOES_PONTE_GHL.includes(key);
                                            return (
                                                <tr key={key} style={status === 'desativada' ? { background: 'rgba(229,72,77,0.04)' } : undefined}>
                                                    <td style={{ ...tdStyle, color: 'var(--text-primary)', fontWeight: 500 }}>{label}</td>
                                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                        <span style={{
                                                            display: 'inline-block', padding: '0.125rem 0.5rem', borderRadius: '0.75rem',
                                                            fontSize: '0.7rem', fontWeight: 600, color: marca.cor, background: marca.fundo,
                                                            border: `1px solid ${marca.cor}33`,
                                                        }}>{marca.texto}</span>
                                                        {status === 'desativada' && (
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2, maxWidth: 210, whiteSpace: 'normal' }}>
                                                                Ninguém pontua.{' '}
                                                                {inv
                                                                    ? `${inv.vivas} linha(s) viva(s)${sobSweep && inv.bridge > 0 ? `, ${inv.bridge} sob o sweep` : ''}.`
                                                                    : 'Contagem de histórico indisponível.'}
                                                                {sobSweep && ' O sweep apaga o histórico ghl-bridge dela.'}
                                                            </div>
                                                        )}
                                                        {status === 'peso-zero' && (
                                                            <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2, maxWidth: 210, whiteSpace: 'normal' }}>
                                                                Pontua e registra a linha, valendo 0.
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <input type="number" min="0" step="1" value={a.default ?? 0} style={numInputStyle}
                                                            onChange={(e) => handleAcaoDefaultChange(key, e.target.value)} />
                                                    </td>
                                                    {cargoColunas.map((c) => {
                                                        const permitido = a.cargos.includes(c.slug);
                                                        const ov = a.override?.[c.slug];
                                                        return (
                                                            <td key={c.slug} style={{ ...tdStyle, textAlign: 'center' }}>
                                                                <input type="checkbox" checked={permitido}
                                                                    onChange={() => handleAcaoCargoToggle(key, c.slug)} />
                                                                {permitido && (
                                                                    <input type="number" min="0" step="1"
                                                                        placeholder={String(a.default ?? 0)}
                                                                        value={ov === undefined || ov === null ? '' : ov}
                                                                        onChange={(e) => handleAcaoOverrideChange(key, c.slug, e.target.value)}
                                                                        style={{ ...numInputStyle, width: 64, marginTop: 4 }} />
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                                    Marque o cargo para ele pontuar a ação. O campo abaixo do check é o valor específico do cargo (vazio = usa o Padrão; 0 = zero pontos).
                                </p>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
                                    <strong style={{ color: ROTULO_STATUS.desativada.cor }}>Desativada</strong> (nenhum cargo marcado) não é o mesmo que{' '}
                                    <strong style={{ color: ROTULO_STATUS['peso-zero'].cor }}>Vale 0</strong>. Desativada tira a ação do cálculo e, nas
                                    ações que o sweep do bridge GHL reconcilia, faz o cron <strong>apagar</strong> as linhas <code>ghl-bridge</code> já
                                    gravadas dela. Vale 0 continua registrando a linha — o evento fica no histórico, valendo zero ponto.
                                </p>
                            </div>

                            {/* ── Bônus de fim de semana / feriado ────────────────────────────
                                Existe para que ninguém precise mexer nos pesos base por causa de
                                campanha de fim de semana. Mexer nos pesos é o que reescrevia
                                histórico antes de a vigência existir. */}
                            <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid var(--border)', borderRadius: 8 }}>
                                <h4 style={{ margin: 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                    Bônus de fim de semana e feriado
                                </h4>
                                <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                        Bônus
                                        <input type="number" min="0" step="5" style={{ ...numInputStyle, width: 80 }}
                                            value={Math.round((((weights.bonus ?? DEFAULT_BONUS).fator ?? 1.5) - 1) * 100)}
                                            onChange={(e) => handleBonusChange('fator', 1 + (parseFloat(e.target.value) || 0) / 100)} />
                                        %
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                        <input type="checkbox"
                                            checked={(weights.bonus ?? DEFAULT_BONUS).fimDeSemana !== false}
                                            onChange={(e) => handleBonusChange('fimDeSemana', e.target.checked)} />
                                        Sábado e domingo
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                                        <input type="checkbox"
                                            checked={(weights.bonus ?? DEFAULT_BONUS).feriados !== false}
                                            onChange={(e) => handleBonusChange('feriados', e.target.checked)} />
                                        Feriados cadastrados
                                    </label>
                                </div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem', marginBottom: 0 }}>
                                    Aplicado automaticamente sobre o valor da ação. Com 50%, uma ação de 1 ponto vale 1,5 no sábado.
                                    Não é mais preciso dobrar os pesos na mão durante campanha.
                                </p>
                            </div>

                            {/* ── Vigência ──────────────────────────────────────────────────── */}
                            <div style={{ marginTop: '1rem', padding: '1rem', border: '1px solid var(--border)', borderRadius: 8 }}>
                                <h4 style={{ margin: 0, marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                                    A partir de quando vale
                                </h4>
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <input type="date" value={vigenciaInicio} style={numInputStyle}
                                        onChange={(e) => setVigenciaInicio(e.target.value)} />
                                    {vigenciaInicio < hojeSP() && (
                                        <span style={{ color: 'var(--danger, #e5484d)', fontSize: '0.8rem', fontWeight: 500 }}>
                                            Data no passado: isso RECALCULA a pontuação de {vigenciaInicio} até hoje.
                                        </span>
                                    )}
                                </div>
                                <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.5rem', marginBottom: 0 }}>
                                    Os pontos já registrados antes desta data não mudam. Deixe em hoje, salvo se estiver
                                    corrigindo de propósito um período que foi calculado errado.
                                </p>
                                {versoes.length > 0 && (
                                    <details style={{ marginTop: '0.75rem' }}>
                                        <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                            Histórico de vigências ({versoes.length})
                                        </summary>
                                        <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', color: 'var(--text-secondary)', fontSize: '0.78rem' }}>
                                            {versoes.map((v) => (
                                                <li key={v.vigencia_inicio}>
                                                    <strong>{v.vigencia_inicio}</strong>
                                                    {v.nota ? ` — ${v.nota}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                            </div>

                            <Button onClick={saveWeights} isLoading={savingWeights} style={{ marginTop: '1rem' }}>
                                {weightsSaved ? 'Salvo!' : 'Salvar Pontuação'}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
            {/* ── FERIADOS ── */}
            <Card>
                <CardHeader
                    title="Feriados"
                    description="Datas que recebem o bônus de pontuação, junto com sábados e domingos. Sábado e domingo não precisam ser cadastrados aqui."
                />
                <CardContent>
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
                        <FormGroup label="Data">
                            <Input type="date" value={novoFeriado.data}
                                onChange={(e) => setNovoFeriado((p) => ({ ...p, data: e.target.value }))} />
                        </FormGroup>
                        <FormGroup label="Nome">
                            <Input type="text" placeholder="Ex.: Aniversário da cidade" value={novoFeriado.nome}
                                onChange={(e) => setNovoFeriado((p) => ({ ...p, nome: e.target.value }))} />
                        </FormGroup>
                        <FormGroup label="Escopo">
                            <Select value={novoFeriado.escopo}
                                onChange={(e) => setNovoFeriado((p) => ({ ...p, escopo: e.target.value }))}>
                                <option value="nacional">Nacional</option>
                                <option value="estadual">Estadual</option>
                                <option value="municipal">Municipal</option>
                                <option value="facultativo">Ponto facultativo</option>
                            </Select>
                        </FormGroup>
                        <Button onClick={adicionarFeriado}>Adicionar</Button>
                    </div>

                    {feriados.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Nenhum feriado cadastrado.</p>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Data</th>
                                    <th style={thStyle}>Nome</th>
                                    <th style={thStyle}>Escopo</th>
                                    <th style={{ ...thStyle, textAlign: 'center' }}>Vale bônus</th>
                                    <th style={thStyle}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {feriados.map((f) => (
                                    <tr key={f.data} style={{ opacity: f.ativo ? 1 : 0.5 }}>
                                        <td style={tdStyle}>{f.data}</td>
                                        <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{f.nome}</td>
                                        <td style={tdStyle}>{f.escopo}</td>
                                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                                            <input type="checkbox" checked={!!f.ativo} onChange={() => alternarFeriado(f)} />
                                        </td>
                                        <td style={tdStyle}>
                                            <button type="button" onClick={() => excluirFeriado(f.data)}
                                                style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}>
                                                remover
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', marginTop: '0.75rem' }}>
                        Desmarcar “Vale bônus” mantém a data cadastrada sem dar pontuação extra.
                        Mudanças aqui afetam o cálculo dos pontos daquele dia daí em diante.
                    </p>
                </CardContent>
            </Card>
            {/* ── AJUSTE MANUAL ── */}
            <Card>
                <CardHeader
                    title="Ajuste Manual de Pontos"
                    description="Adicione ou remova pontos de uma pessoa em uma data específica. Use valores negativos para descontar."
                />
                <CardContent>
                    <div className="grid-2" style={{ marginBottom: '1.25rem' }}>
                        <FormGroup label="Pessoa">
                            <select
                                value={ajuste.pessoa}
                                onChange={(e) => setAjuste(prev => ({ ...prev, pessoa: e.target.value }))}
                                style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: 'var(--text-primary)', padding: '0.5rem 0.75rem', fontSize: '0.875rem' }}
                            >
                                {pessoas.map(p => <option key={p.nome} value={p.nome}>{p.nome}</option>)}
                            </select>
                        </FormGroup>
                        <FormGroup label="Pontos (negativo para descontar)">
                            <Input type="number" step="0.5" value={ajuste.pontos} onChange={(e) => setAjuste(prev => ({ ...prev, pontos: e.target.value }))} />
                        </FormGroup>
                        <FormGroup label="Data">
                            <Input type="date" value={ajuste.data} onChange={(e) => setAjuste(prev => ({ ...prev, data: e.target.value }))} />
                        </FormGroup>
                        <FormGroup label="Observação (obrigatório)">
                            <Input type="text" value={ajuste.observacao} onChange={(e) => setAjuste(prev => ({ ...prev, observacao: e.target.value }))} placeholder="Motivo do ajuste" />
                        </FormGroup>
                    </div>
                    <Button onClick={salvarAjuste} isLoading={savingAjuste}>Salvar Ajuste</Button>
                    {ajusteMsg && (
                        <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: ajusteMsg.startsWith('Erro') || ajusteMsg === 'Observação obrigatória.' || ajusteMsg === 'Informe um valor de pontos.' ? '#f87171' : 'var(--primary-color)' }}>
                            {ajusteMsg}
                        </p>
                    )}

                    {ajustesHistorico.length > 0 && (
                        <div style={{ marginTop: '1.5rem', overflowX: 'auto' }}>
                            <h4 style={{ color: 'var(--text-primary)', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem' }}>Últimos ajustes manuais</h4>
                            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8125rem' }}>
                                <thead>
                                    <tr>
                                        {['Pessoa', 'Pontos', 'Data', 'Observação', ''].map(h => (
                                            <th key={h} style={thStyle}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {ajustesHistorico.map(a => (
                                        <tr key={a.id}>
                                            <td style={tdStyle}>{a.pessoa}</td>
                                            <td style={{ ...tdStyle, color: a.pontos >= 0 ? 'var(--primary-color)' : '#f87171', fontWeight: 600 }}>
                                                {a.pontos >= 0 ? '+' : ''}{a.pontos}
                                            </td>
                                            <td style={tdStyle}>{a.data}</td>
                                            <td style={tdStyle}>{a.observacao}</td>
                                            <td style={tdStyle}>
                                                {!a.observacao?.startsWith('Estorno:') && (
                                                    <button
                                                        onClick={() => estornarAjuste(a)}
                                                        style={{ padding: '0.2rem 0.6rem', borderRadius: '0.3rem', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.75rem' }}
                                                    >
                                                        Estornar
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* ── MIGRAÇÃO HISTÓRICA ── */}
            <Card style={{ borderColor: 'rgba(251,191,36,0.2)', background: 'rgba(251,191,36,0.03)' }}>
                <CardHeader
                    title="Migrar Pontos Históricos"
                    description="Reprocessa todos os agendamentos e vendas existentes e sobrescreve os pontos no ledger com os pesos atuais. Use apenas na implantação inicial ou em situações de emergência."
                />
                <CardContent>
                    <Button
                        onClick={() => setShowMigrateConfirm(true)}
                        isLoading={migrating}
                        style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.3)', color: '#fbbf24' }}
                    >
                        {migrating ? 'Migrando...' : 'Migrar Pontos Históricos'}
                    </Button>
                    {migrateMsg && (
                        <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: migrateMsg.startsWith('Erro') ? '#f87171' : 'var(--primary-color)' }}>
                            {migrateMsg}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* ── MODAL DE CONFIRMAÇÃO ── */}
            {showMigrateConfirm && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '1rem',
                }}>
                    <div style={{
                        background: '#0d1b3e', border: '1px solid rgba(251,191,36,0.3)',
                        borderRadius: '1rem', padding: '2rem', maxWidth: 460, width: '100%',
                        boxShadow: '0 25px 50px rgba(0,0,0,0.6)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                            <span style={{ fontSize: '1.5rem' }}>⚠️</span>
                            <h2 style={{ color: '#fbbf24', fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>
                                Atenção — Migração Histórica
                            </h2>
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>
                            Esta operação <strong style={{ color: 'var(--text-primary)' }}>sobrescreve os pontos</strong> dos registros selecionados com os pesos configurados atualmente.
                        </p>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                            Use o filtro abaixo para limitar o período. Sem datas preenchidas, <strong style={{ color: '#f87171' }}>todo o histórico</strong> será reprocessado.
                        </p>

                        {/* Filtro de período */}
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.625rem', padding: '1rem', marginBottom: '1.5rem' }}>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
                                Filtro de período (opcional)
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                                <div>
                                    <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.3rem' }}>Data início</label>
                                    <input
                                        type="date"
                                        value={migrateInicio}
                                        onChange={(e) => setMigrateInicio(e.target.value)}
                                        style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: 'var(--text-primary)', padding: '0.4rem 0.6rem', fontSize: '0.8125rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '0.75rem', marginBottom: '0.3rem' }}>Data fim</label>
                                    <input
                                        type="date"
                                        value={migrateFim}
                                        onChange={(e) => setMigrateFim(e.target.value)}
                                        style={{ width: '100%', background: 'var(--input-bg)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.4rem', color: 'var(--text-primary)', padding: '0.4rem 0.6rem', fontSize: '0.8125rem', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>
                            {migrateInicio && migrateFim && migrateInicio > migrateFim && (
                                <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#f87171' }}>
                                    Data início deve ser anterior à data fim.
                                </p>
                            )}
                            {(!migrateInicio || !migrateFim) && (
                                <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#f87171' }}>
                                    Período obrigatório — preencha data início e data fim.
                                </p>
                            )}
                            {migrateInicio && migrateFim && migrateInicio <= migrateFim && (
                                <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--primary-color)' }}>
                                    Apenas pontos com data entre {migrateInicio} e {migrateFim} serão removidos e re-emitidos. Pontos fora do período e ajustes manuais não são afetados.
                                </p>
                            )}
                        </div>

                        {/* Filtro de grupo */}
                        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '0.625rem', padding: '1rem', marginBottom: '1.5rem' }}>
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>
                                Grupo a reprocessar
                            </p>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {[
                                    { value: 'todos', label: 'Todos' },
                                    { value: 'closer', label: 'Só Closers' },
                                    { value: 'sdr', label: 'Só SDRs / Pre-Vendas' },
                                ].map(({ value, label }) => (
                                    <button
                                        key={value}
                                        onClick={() => setMigrateGrupo(value)}
                                        style={{
                                            padding: '0.375rem 0.875rem', borderRadius: '0.4rem', fontSize: '0.8125rem', cursor: 'pointer',
                                            border: migrateGrupo === value ? '1px solid rgba(251,191,36,0.5)' : '1px solid rgba(255,255,255,0.1)',
                                            background: migrateGrupo === value ? 'rgba(251,191,36,0.15)' : 'transparent',
                                            color: migrateGrupo === value ? '#fbbf24' : 'var(--text-secondary)',
                                            fontWeight: migrateGrupo === value ? 600 : 400,
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowMigrateConfirm(false)}
                                style={{
                                    padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                                    border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
                                    color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.875rem',
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => { setShowMigrateConfirm(false); migrarPontosHistoricos(); }}
                                disabled={!(migrateInicio && migrateFim && migrateInicio <= migrateFim)}
                                style={{
                                    padding: '0.5rem 1.25rem', borderRadius: '0.5rem',
                                    border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.15)',
                                    color: '#fbbf24', fontSize: '0.875rem', fontWeight: 600,
                                    cursor: (migrateInicio && migrateFim && migrateInicio <= migrateFim) ? 'pointer' : 'not-allowed',
                                    opacity: (migrateInicio && migrateFim && migrateInicio <= migrateFim) ? 1 : 0.4,
                                }}
                            >
                                Confirmar Migração
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── FONTES DE LEAD ── */}
            <Card>
                <CardHeader
                    title="Fontes de Lead"
                    description="Lista de fontes disponíveis no dropdown de Agendamento. Adicione manualmente ou importe do GHL."
                />
                <CardContent>
                    {loadingSources ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
                                {sourcesList.map((fonte) => (
                                    <span key={fonte} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                                        padding: '0.25rem 0.65rem', borderRadius: '9999px',
                                        background: 'var(--emerald-100)', border: '1px solid var(--emerald-200)',
                                        color: 'var(--emerald)', fontSize: '0.78rem', fontWeight: 600,
                                    }}>
                                        {fonte}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveSource(fonte)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1, opacity: 0.7 }}
                                        >×</button>
                                    </span>
                                ))}
                                {!sourcesList.length && <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Nenhuma fonte cadastrada.</p>}
                            </div>

                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                                <Input
                                    value={novaFonteSource}
                                    onChange={(e) => setNovaFonteSource(e.target.value)}
                                    placeholder="Nova fonte (ex: Cold Outbound)"
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSource())}
                                    style={{ maxWidth: 260 }}
                                />
                                <Button type="button" variant="secondary" onClick={handleAddSource} disabled={!novaFonteSource.trim()}>
                                    + Adicionar
                                </Button>
                            </div>

                            <Button onClick={handleSaveSourcesList} disabled={savingSources}>
                                {sourcesSaved ? 'Salvo!' : savingSources ? 'Salvando...' : 'Salvar Fontes'}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ── CONFIGURAÇÃO DE LEADS — GHL ── */}
            <Card>
                <CardHeader
                    title="Configuração de Leads — GHL"
                    description="Defina qual pipeline e stage do GHL cada fonte de lead deve usar."
                />
                <CardContent>
                    {loadingLeadConfig ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
                    ) : (
                        <>
                            {Object.keys(leadConfig).map((fonte) => {
                                const selectedPipeline = pipelines.find(
                                    (p) => p.id === leadConfig[fonte]?.pipelineId
                                );
                                const stageOptions = (selectedPipeline?.stages || []).map((s) => ({
                                    value: s.id,
                                    label: s.name,
                                }));

                                return (
                                    <div
                                        key={fonte}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '120px 1fr 1fr auto',
                                            gap: '0.75rem',
                                            alignItems: 'end',
                                            marginBottom: '1rem',
                                        }}
                                    >
                                        <span style={{ color: 'var(--text-primary)', fontWeight: 500, paddingBottom: '0.5rem' }}>
                                            {fonte}
                                        </span>
                                        <FormGroup label="Pipeline">
                                            <Select
                                                value={leadConfig[fonte]?.pipelineId || ''}
                                                onChange={(e) => handleLeadConfigChange(fonte, 'pipelineId', e.target.value)}
                                                isLoading={pipelines.length === 0}
                                            >
                                                <option value="">Selecione o pipeline</option>
                                                {pipelines.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </Select>
                                        </FormGroup>
                                        <FormGroup label="Stage">
                                            <Select
                                                value={leadConfig[fonte]?.pipelineStageId || ''}
                                                onChange={(e) => handleLeadConfigChange(fonte, 'pipelineStageId', e.target.value)}
                                            >
                                                <option value="">
                                                    {leadConfig[fonte]?.pipelineId ? 'Selecione o stage' : 'Selecione o pipeline primeiro'}
                                                </option>
                                                {stageOptions.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </Select>
                                        </FormGroup>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveFonte(fonte)}
                                            title="Remover fonte"
                                            style={{
                                                alignSelf: 'end',
                                                marginBottom: '0',
                                                padding: '0.5rem 0.625rem',
                                                borderRadius: '0.4rem',
                                                border: '1px solid rgba(248,113,113,0.3)',
                                                background: 'transparent',
                                                color: '#f87171',
                                                cursor: 'pointer',
                                                fontSize: '0.875rem',
                                                lineHeight: 1,
                                            }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                );
                            })}

                            {/* Adicionar nova fonte */}
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem', marginBottom: '1.25rem' }}>
                                <Input
                                    value={novaFonte}
                                    onChange={(e) => setNovaFonte(e.target.value)}
                                    placeholder="Nova fonte (ex: Cold Outbound)"
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFonte())}
                                    style={{ maxWidth: 260 }}
                                />
                                <Button type="button" variant="secondary" onClick={handleAddFonte} disabled={!novaFonte.trim()}>
                                    + Adicionar
                                </Button>
                            </div>

                            <Button onClick={handleSaveLeadConfig} disabled={savingLeadConfig}>
                                {leadConfigSaved ? 'Salvo!' : savingLeadConfig ? 'Salvando...' : 'Salvar Configuração'}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
            </>}

            {/* ── PRODUTOS ── */}
            {showProdutos && <Card>
                <CardHeader
                    title="Produtos"
                    description="Cadastre os produtos vendidos, palavras-chave para identificação automática e comissões personalizadas por cargo."
                />
                <CardContent>
                    {loadingWeights ? (
                        <p style={{ color: 'var(--text-secondary)' }}>Carregando...</p>
                    ) : (
                        <>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '0.875rem' }}>
                                {produtos.map((p) => (
                                    <div key={p.slug} style={{ borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.07)', padding: '0.5rem 0.75rem', fontSize: '0.8125rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                                            <span style={{ color: 'var(--text-primary)', fontWeight: 500, minWidth: 120 }}>{p.label}</span>
                                            <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                                                {p.keywords.length > 0 ? `palavras-chave: ${p.keywords.join(', ')}` : 'fallback (sem palavras-chave)'}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveProduto(p.slug)}
                                                disabled={produtos.length <= 1}
                                                style={{ background: 'none', border: 'none', color: produtos.length <= 1 ? 'var(--text-secondary)' : '#ef4444', cursor: produtos.length <= 1 ? 'default' : 'pointer', fontSize: '0.8125rem', padding: '0.125rem 0.375rem' }}
                                            >
                                                remover
                                            </button>
                                        </div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.72rem' }}>
                                            Percentuais e bônus deste produto são gerenciados em <a href="/comissionamento" style={{ color: 'var(--primary-color)', fontWeight: 600 }}>Comissionamento</a>.
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                                <input
                                    placeholder="Nome do produto"
                                    value={novoProdutoLabel}
                                    onChange={e => setNovoProdutoLabel(e.target.value)}
                                    style={{ flex: '1 1 140px', background: 'var(--input-bg)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: 'var(--text-primary)', padding: '0.375rem 0.625rem', fontSize: '0.8125rem' }}
                                />
                                <input
                                    placeholder="Palavras-chave (separadas por vírgula)"
                                    value={novoProdutoKeywords}
                                    onChange={e => setNovoProdutoKeywords(e.target.value)}
                                    style={{ flex: '2 1 200px', background: 'var(--input-bg)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '0.5rem', color: 'var(--text-primary)', padding: '0.375rem 0.625rem', fontSize: '0.8125rem' }}
                                />
                                <button
                                    onClick={handleAddProduto}
                                    disabled={!novoProdutoLabel.trim()}
                                    style={{ padding: '0.375rem 0.875rem', borderRadius: '0.5rem', border: '1px solid var(--primary-color)', background: 'rgba(0,220,130,0.08)', color: 'var(--primary-color)', cursor: novoProdutoLabel.trim() ? 'pointer' : 'default', opacity: novoProdutoLabel.trim() ? 1 : 0.4, fontSize: '0.8125rem' }}
                                >
                                    Adicionar Produto
                                </button>
                            </div>
                            {syncGhl && (
                                <p style={{
                                    margin: '0 0 .75rem', padding: '.6rem .8rem', borderRadius: '.45rem',
                                    fontSize: '.82rem', lineHeight: 1.5,
                                    border: `1px solid ${syncGhl.tipo === 'erro' ? 'var(--danger)' : 'var(--emerald)'}`,
                                    background: syncGhl.tipo === 'erro' ? 'rgba(220,38,38,.08)' : 'var(--emerald-100)',
                                    color: syncGhl.tipo === 'erro' ? 'var(--danger)' : 'var(--ink-soft)',
                                }}>{syncGhl.texto}</p>
                            )}
                            <Button onClick={saveWeights} isLoading={savingWeights}>
                                {weightsSaved ? 'Salvo!' : 'Salvar Produtos'}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>}
        </div>
        </div>
        </>
    );
}

// ─── Estilos inline de tabela ─────────────────────────────────────────────────

const thStyle = {
    padding: '0.5rem 0.75rem',
    background: 'rgba(255,255,255,0.04)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: 'var(--text-secondary)',
    fontWeight: 500,
    textAlign: 'left',
    whiteSpace: 'nowrap',
};

const tdStyle = {
    padding: '0.375rem 0.75rem',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    color: 'var(--text-secondary)',
};

const numInputStyle = {
    width: '72px',
    background: 'var(--input-bg)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '0.375rem',
    color: 'var(--text-primary)',
    padding: '0.25rem 0.5rem',
    fontSize: '0.8125rem',
    textAlign: 'center',
};
